import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ShieldCheck, LogIn, Loader2, Smartphone, ArrowRight } from "lucide-react";
import { api, saveSession, errMsg, faDigits } from "../lib/api";

// ورود فقط با شماره موبایل و کد یک‌بار مصرف پیامکی (مدیر و کاربران)
export default function Login() {
  const [step, setStep] = useState("mobile"); // mobile | code
  const [mobile, setMobile] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const nav = useNavigate();

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const requestCode = async (e) => {
    e?.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/otp/request", { mobile });
      toast.success(data.message);
      setStep("code");
      setCooldown(data.resend_in || 60);
    } catch (err) {
      const wait = /(\d+)\s*ثانیه/.exec(errMsg(err, ""));
      if (err?.response?.status === 429 && wait) setCooldown(Number(wait[1]));
      toast.error(errMsg(err, "ارسال کد ناموفق بود"));
    } finally {
      setLoading(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/otp/verify", { mobile, code });
      saveSession({ token: data.token, role: data.role });
      toast.success("ورود موفقیت‌آمیز بود");
      nav(data.role === "admin" ? "/" : "/panel", { replace: true });
    } catch (err) {
      toast.error(errMsg(err, "خطا در ورود"));
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full px-4 py-3 rounded-xl border border-slate-300 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all bg-white text-center tracking-widest";

  return (
    <div className="min-h-screen relative overflow-hidden grid place-items-center bg-slate-50" dir="rtl">
      <div className="blob w-96 h-96 bg-teal-300" style={{ top: "-100px", right: "-100px" }} />
      <div className="blob w-96 h-96 bg-indigo-300" style={{ bottom: "-100px", left: "-100px", animationDelay: "-6s" }} />

      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="relative w-full max-w-md p-8 md:p-10 mx-4"
      >
        <div className="bg-white/90 backdrop-blur-xl border border-slate-200/80 rounded-3xl shadow-xl p-8">
          <div className="flex flex-col items-center mb-6">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-teal-500 to-teal-700 grid place-items-center text-white shadow-lg mb-3">
              <ShieldCheck size={28} />
            </div>
            <h1 className="text-2xl font-bold text-slate-900">سامانه استعلام هویت</h1>
            <p className="text-sm text-slate-500 mt-1">
              {step === "mobile" ? "شماره موبایل خود را وارد کنید" : "کد یک‌بار مصرف ارسال‌شده را وارد کنید"}
            </p>
          </div>

          {step === "mobile" ? (
            <form onSubmit={requestCode} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">شماره موبایل</label>
                <input
                  data-testid="login-mobile-input"
                  type="tel"
                  inputMode="numeric"
                  dir="ltr"
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value)}
                  className={inputCls}
                  placeholder="09123456789"
                  required
                />
              </div>
              <button
                data-testid="login-request-otp-button"
                type="submit"
                disabled={loading || !mobile}
                className="w-full bg-teal-600 hover:bg-teal-700 disabled:bg-teal-300 text-white font-medium px-4 py-3 rounded-xl transition-all flex items-center justify-center gap-2 active:scale-[0.98]"
              >
                {loading ? <Loader2 className="animate-spin" size={18} /> : <Smartphone size={18} />}
                <span>ارسال کد ورود</span>
              </button>
            </form>
          ) : (
            <form onSubmit={verify} className="space-y-4">
              <div className="text-sm text-slate-600 text-center" dir="ltr">{faDigits(mobile)}</div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">کد ۶ رقمی</label>
                <input
                  data-testid="login-otp-input"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  dir="ltr"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  className={`${inputCls} text-xl`}
                  placeholder="------"
                  required
                />
              </div>
              <button
                data-testid="login-submit-button"
                type="submit"
                disabled={loading || code.length < 4}
                className="w-full bg-teal-600 hover:bg-teal-700 disabled:bg-teal-300 text-white font-medium px-4 py-3 rounded-xl transition-all flex items-center justify-center gap-2 active:scale-[0.98]"
              >
                {loading ? <Loader2 className="animate-spin" size={18} /> : <LogIn size={18} />}
                <span>ورود</span>
              </button>
              <div className="flex items-center justify-between text-sm">
                <button type="button" onClick={() => setStep("mobile")} className="text-slate-500 flex items-center gap-1">
                  <ArrowRight size={14} /> تغییر شماره
                </button>
                <button
                  type="button"
                  disabled={cooldown > 0 || loading}
                  onClick={requestCode}
                  className="text-teal-700 disabled:text-slate-400"
                >
                  {cooldown > 0 ? `ارسال مجدد (${faDigits(cooldown)})` : "ارسال مجدد کد"}
                </button>
              </div>
            </form>
          )}
          <div className="text-xs text-center text-slate-400 mt-6 leading-relaxed">
            ثبت‌نام عمومی وجود ندارد؛ حساب کاربری توسط مدیر ایجاد می‌شود.
          </div>
        </div>
      </motion.div>
    </div>
  );
}
